/**
 * Formatação de instante — escrita à mão, de propósito.
 *
 * `Intl.DateTimeFormat` não é determinístico entre ambientes: a saída varia com
 * a versão do ICU do runtime, então o mesmo instante sai diferente em dev, em
 * teste e em produção. Isso quebra asserção de teste e, pior, faz duas telas do
 * produto discordarem sobre a mesma data. É a mesma razão pela qual dinheiro é
 * formatado à mão neste projeto.
 *
 * Fica em UM lugar. Duas cópias divergem, e o bug sobrevive ao próprio conserto.
 *
 * UTC explícito no texto: o dono opera com fusos diferentes dos servidores, e
 * uma hora sem fuso é uma hora errada esperando acontecer.
 */

function doisDigitos(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** `25/08/2026 12:00 UTC` */
export function formatInstant(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return (
    `${doisDigitos(d.getUTCDate())}/${doisDigitos(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}` +
    ` ${doisDigitos(d.getUTCHours())}:${doisDigitos(d.getUTCMinutes())} UTC`
  );
}

/** `25/08/2026` — para prazo, onde a hora não muda a decisão. */
export function formatDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${doisDigitos(d.getUTCDate())}/${doisDigitos(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
}

/**
 * Tempo relativo, para "quando isso aconteceu".
 *
 * Devolve `null` quando não há data: quem chama decide o texto de ausência, em
 * vez de receber um "agora" inventado.
 */
export function formatRelative(iso: string | null, now: Date): string | null {
  if (iso === null) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;

  const segundos = Math.round((now.getTime() - d.getTime()) / 1000);
  const futuro = segundos < 0;
  const abs = Math.abs(segundos);

  const texto =
    abs < 60
      ? 'menos de um minuto'
      : abs < 3600
        ? `${Math.floor(abs / 60)} min`
        : abs < 86_400
          ? `${Math.floor(abs / 3600)} h`
          : `${Math.floor(abs / 86_400)} d`;

  return futuro ? `em ${texto}` : `há ${texto}`;
}
